import * as React from "react"
import { RouteComponentProps } from "@gatsbyjs/reach-router"
import { Waypoint } from "react-waypoint"
import {
  MerlinSourceEventCreatedDocument,
  MerlinSourceEventCreatedSubscription,
  useMerlinSourcingDetailsQuery,
  usePaginatedMerlinSourceEventsByResourceIdQuery,
  usePaginatedMerlinSourceEventsByResourceIdForPoolingQuery,
  PaginatedMerlinSourceEventsByResourceIdDocument,
  PaginatedMerlinSourceEventsByResourceIdQueryVariables,
} from "./queries.generated"
import { Event } from "./event"
import { ErrorAlert } from "../shared/error-alert"
import * as styles from "./styles.css"
import { ResourceDataButton } from "./resource-data-button"
import { DataLayerEventsLoading } from "./events-loading"
import { SiteDataLayerStatus } from "./site-status"
import { DataLayerNotActive } from "./not-active"
import { useFlags } from "launchdarkly-react-client-sdk"
import {
  MachinePricingTier,
  MerlinSourceEvent,
  MerlinSourceEventResourceType,
} from "@gatsbyjs/graphql"
import { AnchorButton, Spacer } from "gatsby-interface"
import { useApolloClient } from "react-apollo"

const EVENTS_LIMIT = 20

interface DataLayerDetailsProps extends RouteComponentProps {
  organizationId: string
  siteId: string
  sourceEventId: string
}

export function DataLayerDetails({
  organizationId,
  siteId,
}: DataLayerDetailsProps) {
  const client = useApolloClient()
  const flags = useFlags()

  const sourcing = useMerlinSourcingDetailsQuery({
    variables: { siteId, organizationId },
  })

  const dataLayerIsEnabledForSite = Boolean(
    sourcing.data?.siteDetails?.merlinProductionSourcingEnabled
  )

  const dataLayerApiEnabledForSite = Boolean(
    sourcing.data?.siteDetails?.merlinGraphQLApiEnabled
  )

  const apiEndpoint = `${sourcing.data?.siteDetails?.sitePrefix}-prod.valhalla-api.io/graphql`
  const apiLink = `https://${apiEndpoint}`
  const schemaLink = `https://studio.apollographql.com/sandbox/explorer?endpoint=${apiLink}`

  const sharedVariables = {
    siteId,
    resourceType: MerlinSourceEventResourceType.Site,
    pagination: {
      limit: EVENTS_LIMIT,
    },
  }

  const eventsData = usePaginatedMerlinSourceEventsByResourceIdQuery({
    variables: sharedVariables,
    skip: !dataLayerIsEnabledForSite,
    notifyOnNetworkStatusChange: true,
  })

  const incomingEventsData =
    usePaginatedMerlinSourceEventsByResourceIdForPoolingQuery({
      variables: {
        siteId,
        pagination: {
          limit: EVENTS_LIMIT,
        },
      },
      skip: !eventsData.data?.paginatedMerlinSourceEventsByResourceId,
      fetchPolicy: "network-only",
      pollInterval: 5000,
    })

  const incomingEvents =
    incomingEventsData.data?.paginatedMerlinSourceEventsByResourceIdForPooling
      ?.merlinSourceEvents

  React.useEffect(() => {
    const eventsQueryInfo = {
      query: PaginatedMerlinSourceEventsByResourceIdDocument,
      variables: {
        siteId,
        resourceType: MerlinSourceEventResourceType.Site,
        pagination: {
          limit: EVENTS_LIMIT,
        },
      },
    }

    if (incomingEvents) {
      try {
        const currentEventsData = client.readQuery(eventsQueryInfo)
        const currentEvents =
          currentEventsData.paginatedMerlinSourceEventsByResourceId
            .merlinSourceEvents
        const currentEventsIds = currentEvents.map(
          (event: MerlinSourceEvent) => event.id
        )

        const diff =
          incomingEvents.filter(
            event => !currentEventsIds.includes(event.id)
          ) || []

        if (diff.length > 0) {
          client.writeQuery({
            query: PaginatedMerlinSourceEventsByResourceIdDocument,
            variables: sharedVariables,
            data: {
              paginatedMerlinSourceEventsByResourceId: {
                __typename: "MerlinSourceEventResults",
                merlinSourceEvents: [...diff, ...currentEvents],
                pageInfo:
                  currentEventsData.paginatedMerlinSourceEventsByResourceId
                    .pageInfo,
              },
            },
          })
        }
      } catch (e) {
        console.error(e)
      }
    }
  }, incomingEvents)

  const events =
    eventsData.data?.paginatedMerlinSourceEventsByResourceId
      ?.merlinSourceEvents || []

  const initialLoading = eventsData.networkStatus === 1
  const loadMoreLoading = eventsData.networkStatus === 3

  const loadMore = () => {
    const currentPageInfo =
      eventsData?.data?.paginatedMerlinSourceEventsByResourceId?.pageInfo

    if (
      !currentPageInfo?.hasNextPage ||
      !currentPageInfo?.endCursor ||
      loadMoreLoading
    ) {
      return
    }

    eventsData.fetchMore({
      variables: {
        siteId,
        pagination: {
          limit: EVENTS_LIMIT,
          endCursor: currentPageInfo.endCursor,
        },
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        const newEvents =
          fetchMoreResult?.paginatedMerlinSourceEventsByResourceId
            ?.merlinSourceEvents
        const pageInfo =
          fetchMoreResult?.paginatedMerlinSourceEventsByResourceId?.pageInfo

        if (!newEvents || newEvents.length === 0) {
          return prev
        }

        return {
          ...prev,
          paginatedMerlinSourceEventsByResourceId: {
            __typename: "MerlinSourceEventResults",
            merlinSourceEvents: [
              ...(prev.paginatedMerlinSourceEventsByResourceId
                ?.merlinSourceEvents || []),
              ...newEvents,
            ],
            pageInfo,
          },
        }
      },
    })
  }

  const planTier =
    sourcing?.data?.organizationDetails?.billing?.plan?.baseFeatures?.tier
  const isFreeTierPlan = planTier === MachinePricingTier.Free
  const isAgencyOrEnterpriseTierPlan =
    planTier &&
    [MachinePricingTier.Agency, MachinePricingTier.Enterprise].includes(
      planTier
    )

  React.useEffect(() => {
    const unsubscribe = eventsData.subscribeToMore<
      MerlinSourceEventCreatedSubscription,
      PaginatedMerlinSourceEventsByResourceIdQueryVariables
    >({
      document: MerlinSourceEventCreatedDocument,
      variables: sharedVariables,
      updateQuery: (prev, { subscriptionData }) => {
        const newEvent = subscriptionData.data?.merlinSourceEventCreated

        if (!newEvent) return prev

        return {
          ...prev,
          paginatedMerlinSourceEventsByResourceId: {
            __typename: "MerlinSourceEventResults",
            ...prev.paginatedMerlinSourceEventsByResourceId,
            merlinSourceEvents: [
              newEvent,
              ...(prev.paginatedMerlinSourceEventsByResourceId
                ?.merlinSourceEvents || []),
            ],
          },
        }
      },
    })
    return () => {
      unsubscribe()
    }
  }, [])

  // before knowing if sourcing is or not enabled renders nothing...
  if (sourcing.loading) {
    return null
  }

  // ...except
  if (sourcing.error) {
    return <ErrorAlert>{sourcing.error.message}</ErrorAlert>
  }

  if (
    isFreeTierPlan ||
    (!isFreeTierPlan && !flags.acceleratedSourcingEarlyAccess) ||
    !dataLayerIsEnabledForSite
  ) {
    return (
      <DataLayerNotActive
        siteId={siteId}
        organizationId={organizationId}
        hasEarlyAccess={flags.acceleratedSourcingEarlyAccess}
        isAgencyOrEnterpriseTierPlan={isAgencyOrEnterpriseTierPlan}
        isFreeTierPlan={isFreeTierPlan}
      />
    )
  }

  return (
    <div>
      <div className={styles.top}>
        <header className={styles.header}>
          <div className={styles.headingWrapper}>
            <h2 className={styles.heading}>Content Hub</h2>
            <SiteDataLayerStatus
              siteId={siteId}
              isEnabled={dataLayerIsEnabledForSite}
            />
          </div>

          {dataLayerApiEnabledForSite && (
            <span className={styles.apiLink}>{apiEndpoint}</span>
          )}
        </header>
        <div>
          {dataLayerApiEnabledForSite && (
            <>
              <AnchorButton href={schemaLink} variant="SECONDARY">
                Browse schema
              </AnchorButton>
              <Spacer size={4} direction="horizontal" />
            </>
          )}
          <ResourceDataButton siteId={siteId} />
        </div>
      </div>

      <h3 className={styles.subHeading}>Timeline</h3>

      {eventsData.networkStatus === 1 && <DataLayerEventsLoading />}

      {eventsData.error && <ErrorAlert>{eventsData.error.message}</ErrorAlert>}

      {events && (
        <div>
          {events.map((event, idx, arr) => {
            if (idx === arr.length - EVENTS_LIMIT) {
              return (
                <div key={event.id}>
                  <WaypointWrapper
                    key={event.id}
                    onEnter={loadMore}
                    loading={initialLoading || loadMoreLoading}
                    hasNextPage={
                      eventsData.data?.paginatedMerlinSourceEventsByResourceId
                        ?.pageInfo?.hasNextPage
                    }
                  />
                  <Event event={event} />
                </div>
              )
            }

            return <Event key={event.id} event={event} />
          })}
        </div>
      )}

      {loadMoreLoading && <DataLayerEventsLoading />}
    </div>
  )
}

type WaypointWrapperProps = {
  loading: boolean
  hasNextPage?: boolean | null
  onEnter: () => void
}

function WaypointWrapper({
  loading,
  hasNextPage,
  onEnter,
}: WaypointWrapperProps) {
  if (loading || !hasNextPage) {
    return null
  }

  return <Waypoint onEnter={onEnter} />
}
